import React, { useState, useEffect } from 'react';
import { Search, Instagram, Loader2, X, Check, Globe, CreditCard, Copy, ExternalLink, BarChart3, DollarSign, TrendingUp, Trash2 } from 'lucide-react';
import { translations, languages } from '../../translations';
import { hikerApi } from '../../services/hikerApi';
import { supabase } from '../../lib/supabase';
import { products } from '../../stripe-config';
import { getPlanBadgeStyles } from '../../utils/planStyles';
import { plans } from '../../data';

interface ClosingProps {
  isDarkMode: boolean;
  language: string;
}

interface ProfilePreview {
  username: string;
  name: string;
  avatar: string;
  followers: string;
  following: string;
  posts: number;
  biography: string;
  isVerified: boolean;
  isPrivate: boolean;
}

interface PaymentLink {
  id: string;
  username: string;
  plan: string;
  period: string;
  language: string;
  url: string;
  createdAt: string;
  status: 'pending' | 'paid';
}

export function Closing({ isDarkMode, language }: ClosingProps) {
  const t = translations[language as keyof typeof translations];
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [profilePreview, setProfilePreview] = useState<ProfilePreview | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [isCopied, setIsCopied] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(true);
  const [stats, setStats] = useState({
    totalSent: 0,
    totalClosed: 0,
    totalRevenue: 0,
    totalCommission: 0
  });

  useEffect(() => {
    loadPaymentLinks();
  }, []);

  const loadPaymentLinks = async () => {
    try {
      setIsLoadingLinks(true);
      const { data, error } = await supabase
        .from('payment_links')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate stats
      const links = data.map(link => ({
        id: link.id,
        username: link.username,
        plan: link.plan,
        period: link.period,
        language: link.language,
        url: link.url,
        createdAt: link.created_at,
        status: link.status as 'pending' | 'paid'
      }));

      calculateStats(links);
      
      setPaymentLinks(data.map(link => ({
        id: link.id,
        username: link.username,
        plan: link.plan,
        period: link.period,
        language: link.language,
        url: link.url,
        createdAt: link.created_at,
        status: link.status as 'pending' | 'paid'
      })));
    } catch (err) {
      console.error('Error loading payment links:', err);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const calculateStats = (links: PaymentLink[]) => {
    let totalSent = links.length;
    let totalClosed = links.filter(link => link.status === 'paid').length;
    let totalRevenue = 0; 
    let totalValue = 0;
    
    links.forEach(link => {
      // Calculate the value of all links (sent)
      const product = products[link.plan as keyof typeof products];
      if (product) {
        const priceObj = link.period === 'annual' 
          ? product.EUR.annual 
          : product.EUR.monthly;
        
        // Parse price from string (e.g., "56€" -> 56)
        const price = parseInt(priceObj.price.replace(/[^0-9]/g, ''));
        
        // For annual plans, multiply by 12 to get annual value
        const annualValue = link.period === 'annual' ? price * 12 : price;
        
        totalValue += annualValue;
      }
      
      // Calculate revenue from paid links only
      if (link.status === 'paid') {
        const product = products[link.plan as keyof typeof products];
        if (product) {
          const priceObj = link.period === 'annual' 
            ? product.EUR.annual 
            : product.EUR.monthly;
          
          // Parse price from string (e.g., "56€" -> 56)
          const price = parseInt(priceObj.price.replace(/[^0-9]/g, ''));
          
          // For annual plans, multiply by 12 to get annual value
          const annualValue = link.period === 'annual' ? price * 12 : price;
          
          totalRevenue += annualValue;
        }
      }
    });
    
    // Commission is 20% of revenue
    const totalCommission = totalRevenue * 0.2;
    
    setStats({
      totalSent,
      totalClosed,
      totalRevenue, 
      totalCommission,
      totalValue
    });
  };

  const handleDeletePaymentLink = async (linkId: string) => {
    try {
      // Delete from database first
      const { error } = await supabase
        .from('payment_links')
        .delete()
        .eq('id', linkId);

      if (error) {
        console.error('Database deletion error:', error);
        throw error;
      }

      // Update UI after successful database deletion
      setPaymentLinks(prev => {
        const updated = prev.filter(link => link.id !== linkId);
        calculateStats(updated);
        return updated;
      });
      
    } catch (err) {
      console.error('Error deleting payment link:', err);
      setError('Failed to delete payment link');
    }
  };

  const handleSearch = async () => {
    if (!username.trim()) {
      setError('Please enter an Instagram username');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const cleanUsername = username.replace('@', '').trim();
      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error || !result.data) {
        setError(result.message || 'Failed to fetch account information');
        return;
      }

      setProfilePreview({
        username: '@' + result.data.username,
        name: result.data.fullName || result.data.username,
        avatar: result.data.profilePicUrl || 'https://via.placeholder.com/150',
        followers: result.data.followers || '0',
        following: result.data.following || '0',
        posts: result.data.posts || 0,
        biography: result.data.biography || '',
        isVerified: result.data.isVerified || false,
        isPrivate: result.data.isPrivate || false
      });

      setShowModal(true);
    } catch (err) {
      console.error('Error searching account:', err);
      setError('Failed to search account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handleSelectPlan = (planName: string) => {
    setSelectedPlan(planName);
  };

  const handleGenerateLink = async () => {
    if (!selectedPlan || !profilePreview) return;

    setIsGeneratingLink(true);
    
    try {
      // Generate a valid UUID for this closing link
      const closingId = crypto.randomUUID();
      
      // Create a URL with the profile and plan information
      const params = new URLSearchParams();
      params.append('username', profilePreview.username);
      params.append('plan', selectedPlan);
      params.append('period', isAnnual ? 'annual' : 'monthly');
      params.append('id', closingId);
      params.append('lang', selectedLanguage);
      
      // Generate the payment link
      // Use app.amplr.ai as the domain for payment links
      const paymentUrl = `https://app.amplr.ai/payment?${params.toString()}`;
      setPaymentLink(paymentUrl);

      // Save to database
      const { data, error } = await supabase
        .from('payment_links')
        .insert({
          id: closingId,
          username: profilePreview.username,
          plan: selectedPlan,
          period: isAnnual ? 'annual' : 'monthly',
          language: selectedLanguage,
          url: paymentUrl,
          status: 'pending',
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      if (data) {
        const newLink: PaymentLink = {
          id: data.id,
          username: data.username,
          plan: data.plan,
          period: data.period,
          language: data.language,
          url: data.url,
          createdAt: data.created_at,
          status: data.status
        };
        
        setPaymentLinks(prev => [newLink, ...prev]);
      }
    } catch (err) {
      console.error('Error generating payment link:', err);
      setError('Failed to generate payment link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy link:', err);
        });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setStep(1);
    setProfilePreview(null);
    setSelectedPlan(null);
    setPaymentLink(null);
  };

  return (
    <div className="p-8">
      <h1 className={`text-2xl font-semibold mb-6 ${
        isDarkMode ? 'text-gray-200' : 'text-gray-900'
      }`}>
        Closing
      </h1>

      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>
          Generate Payment Link
        </h2>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Enter Instagram username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={handleKeyPress}
            className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
              isDarkMode
                ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                : 'bg-gray-50 text-gray-900 border-gray-200'
            } focus:outline-none focus:ring-2 focus:ring-orange-500`}
          />
          <Instagram size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        <button
          onClick={handleSearch}
          disabled={isLoading || !username.trim()}
          className="w-full bg-orange-500 text-white rounded-lg py-2.5 font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <>
              <Search size={16} />
              <span>Search Account</span>
            </>
          )}
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-lg ${
            isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className={`text-lg font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>
                {step === 1 ? 'Instagram Profile' : 'Select Plan'}
              </h3>
              <button 
                onClick={closeModal}
                className={`p-1 rounded-full ${
                  isDarkMode ? 'hover:bg-[#2b2b2b]' : 'hover:bg-gray-100'
                }`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            
            <div className="p-4">
              {step === 1 && profilePreview && (
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <img
                      src={profilePreview.avatar}
                      alt={profilePreview.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                          {profilePreview.name}
                        </h4>
                        {profilePreview.isVerified && (
                          <span className="text-blue-500">
                            <Check size={16} className="fill-blue-500" />
                          </span>
                        )}
                        {profilePreview.isPrivate && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                          }`}>
                            Private
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {profilePreview.username}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 py-3 border-y border-gray-200 dark:border-gray-800">
                    <div className="text-center">
                      <div className={`text-lg font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {profilePreview.posts}
                      </div>
                      <div className={`text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        posts
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {profilePreview.followers}
                      </div>
                      <div className={`text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        followers
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {profilePreview.following}
                      </div>
                      <div className={`text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        following
                      </div>
                    </div>
                  </div>

                  {profilePreview.biography && (
                    <div className={`text-sm ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {profilePreview.biography.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}
                          {index < profilePreview.biography.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className={`inline-flex rounded-lg p-0.5 mb-4 ${
                    isDarkMode 
                      ? 'bg-[#2b2b2b]' 
                      : 'bg-gray-100 border border-gray-200'
                  }`}>
                    <button
                      onClick={() => setIsAnnual(false)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        !isAnnual
                          ? isDarkMode
                            ? 'bg-[#1c1c1c] text-white'
                            : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                          : isDarkMode
                            ? 'text-gray-400'
                            : 'text-gray-500'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setIsAnnual(true)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isAnnual
                          ? isDarkMode
                            ? 'bg-[#1c1c1c] text-white'
                            : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                          : isDarkMode
                            ? 'text-gray-400'
                            : 'text-gray-500'
                      }`}
                    >
                      Annual
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {plans.map((plan) => {
                      const isSelected = selectedPlan === plan.name;
                      const savings = (plan.annualSavings?.[language as keyof typeof plan.annualSavings] || '') as string;
                      const saveAnnuallyText = t.save_annually ? t.save_annually.replace('{savings}', savings) : '';

                      return (
                        <div
                          key={plan.name}
                          onClick={() => handleSelectPlan(plan.name)}
                          className={`rounded-xl border p-4 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-orange-500 ring-1 ring-orange-500'
                              : isDarkMode 
                                ? 'bg-[#1c1c1c] border-[#2b2b2b] hover:border-gray-700' 
                                : 'bg-white border-[#e5e7eb] hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className={`text-lg font-semibold ${
                                isDarkMode ? 'text-gray-200' : 'text-gray-900'
                              }`}>{plan.name}</h3>
                              <div className="mt-1">
                                <span className={`text-xl font-bold ${
                                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                                }`}>
                                  {isAnnual 
                                    ? plan.priceAnnual[selectedLanguage as keyof typeof plan.priceAnnual]
                                    : plan.priceMonthly[selectedLanguage as keyof typeof plan.priceMonthly]
                                  }
                                </span>
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                  {t.per_month}
                                </span>
                                {isAnnual && savings && saveAnnuallyText && (
                                  <div className="mt-1 text-sm text-emerald-500 font-medium">
                                    {saveAnnuallyText}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border ${
                              isSelected
                                ? 'border-orange-500 bg-orange-500 flex items-center justify-center'
                                : isDarkMode
                                  ? 'border-gray-600'
                                  : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <Check size={12} className="text-white" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 mb-4">
                    <label className={`block text-sm font-medium mb-1.5 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Language
                    </label>
                    <div className="relative">
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm appearance-none ${
                          isDarkMode
                            ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                            : 'bg-gray-50 text-gray-900 border-gray-200'
                        } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                      >
                        {languages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.flag} {lang.name}
                          </option>
                        ))}
                      </select>
                      <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {paymentLink && (
                    <div className={`mt-4 p-4 rounded-lg ${
                      isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>Payment Link</h4>
                        <button
                          onClick={handleCopyLink}
                          className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1"
                        >
                          {isCopied ? (
                            <>
                              <Check size={14} />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy size={14} />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className={`text-sm break-all ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {paymentLink}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-between p-4 border-t border-gray-200 dark:border-gray-800">
              {step === 1 ? (
                <div className="flex justify-end w-full">
                  <button
                    onClick={handleNextStep}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Next
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={handlePrevStep}
                    className={`px-4 py-2 rounded-lg text-sm ${
                      isDarkMode 
                        ? 'text-gray-300 hover:bg-[#2b2b2b]' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Back
                  </button>
                  
                  <button
                    onClick={() => paymentLink ? closeModal() : handleGenerateLink()}
                    disabled={!selectedPlan || isGeneratingLink}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      paymentLink 
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }`}
                  >
                    {isGeneratingLink ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : paymentLink ? (
                      <>
                        <Check size={14} />
                        <span>Finish</span>
                      </>
                    ) : (
                      <>
                        <CreditCard size={14} />
                        <span>Generate Payment Link</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8 mb-8">
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
            }`}>
              <CreditCard size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <h3 className={`text-sm font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Payment Links Sent
            </h3>
          </div>
          <p className={`text-2xl font-semibold flex flex-col ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {stats.totalSent}
            <span className="text-sm text-gray-500">
              ({stats.totalValue?.toLocaleString()}€)
            </span>
          </p>
        </div>
        
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
            }`}>
              <BarChart3 size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <h3 className={`text-sm font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Deals Closed
            </h3>
          </div>
          <p className={`text-2xl font-semibold flex flex-col ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {stats.totalClosed}
            <span className="text-sm text-gray-500">
              ({stats.totalRevenue?.toLocaleString()}€)
            </span>
          </p>
        </div>
        
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
            }`}>
              <DollarSign size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <h3 className={`text-sm font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Total Revenue
            </h3>
          </div>
          <p className={`text-2xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {stats.totalRevenue.toLocaleString()}€
          </p>
        </div>
        
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${
              isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
            }`}>
              <TrendingUp size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <h3 className={`text-sm font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Commission (20%)
            </h3>
          </div>
          <p className={`text-2xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {stats.totalCommission.toLocaleString()}€
          </p>
        </div>
      </div>
      
      {/* Payment Links History */}
      <div className={`mt-8 p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>
          Generated Payment Links
        </h2>
        
        {isLoadingLinks ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : paymentLinks.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <th className="pb-2">Username</th>
                  <th className="pb-2">Plan</th>
                  <th className="pb-2">Period</th>
                  <th className="pb-2">Language</th>
                  <th className="pb-2">Created</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paymentLinks.map((link) => (
                  <tr key={link.id} className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <td className="py-3">{link.username}</td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getPlanBadgeStyles(link.plan)}`}>
                        {link.plan.replace('Amplr ', '')}
                      </span>
                    </td>
                    <td className="py-3 capitalize">{link.period}</td>
                    <td className="py-3">{languages.find(l => l.code === link.language)?.flag || link.language}</td>
                    <td className="py-3">{new Date(link.createdAt).toLocaleString()}</td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        link.status === 'paid' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {link.status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(link.url);
                            setIsCopied(true);
                            setTimeout(() => setIsCopied(false), 2000);
                          }}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="Copy link"
                        >
                          <Copy size={14} />
                        </button>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="Open link"
                        >
                          <ExternalLink size={14} />
                        </a>
                        <button
                          onClick={() => handleDeletePaymentLink(link.id)}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="Delete link"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
        <div className={`mt-8 p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#252525] border-[#333333]' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <p className={`text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No payment links generated yet
          </p>
        </div>
        )}
      </div>
    </div>
  );
}